import React, { useState, useEffect } from "react";
import { Table, Form, Input, DatePicker, Select, notification, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { INRFormatter, numberWithCommas } from "../../shared/services/Utility";
import API_SERVICE from '../../shared/services/api-service';
import { RECONCILE_NUMBER_REGEX, RECONCILE_PERCENTAGE_REGEX } from 'shared/constants/AppConstants';
import "./index.scss";

interface DataType {
  key: string;
  disbursalId: string;
  caseId: string;
  lenderIdentifier: string;
  lenderName: string;
  borrowerName: string;
  loanProductName: string;
  disbursalDate: string;
  lenderDisbursementDate?: string;
  disbursedAmount: number;
  lenderDisbursedAmount?: number;
  insuranceAmount: number;
  salesUserHierarchy: string;
  status: string;
  isInvoice?: boolean;
  revenuePercentage?: number;
  revenueAmount?: number;
  invoiceNo?: string;
  invoiceDate?: string;
}

// Utility to format numeric fields for form display
function formatReconciliationFormValues(record: any) {
  return {
    ...record,
    lenderDisbursedAmount: record.lenderDisbursedAmount !== undefined ? numberWithCommas(record.lenderDisbursedAmount) : '',
    insuranceAmount: record.insuranceAmount !== undefined ? numberWithCommas(record.insuranceAmount) : '',
    revenueAmount: record.revenueAmount !== undefined ? numberWithCommas(record.revenueAmount) : '',
    revenuePct: record.revenuePercentage !== undefined ? record.revenuePercentage.toString() : '',
    disbursedAmount: record.disbursedAmount !== undefined ? numberWithCommas(record.disbursedAmount) : '',
    invoiceNo: record.invoiceNo || '',
    invoiceDate: record.invoiceDate ? moment(record.invoiceDate) : null,
    lenderDisbursementDate: record.lenderDisbursementDate ? moment(record.lenderDisbursementDate) : null
  };
}

interface Props {
  filterValues: any;
  onReconcileDataSequenceComplete?: () => void;
}

const ReconciliationIssues: React.FC<Props> = ({ filterValues, onReconcileDataSequenceComplete }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [formType, setFormType] = useState<"reconcile" | "issue" | null>(null);
  const [mainForm] = Form.useForm();
  const { appColors }: any = useSelector((state: RootState) => state.common);
  const { searchParam } = useSelector((state: RootState) => state.reconciliation);
  const [statusMap, setStatusMap] = useState<{ [key: string]: string }>({});
  const [data, setData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    API_SERVICE.getReconcileDisbursedCases({
      pageNo,
      pageSize,
      status: 'ISSUE',
      searchParam,
      ...filterValues // Include filter values in the API call
    })
      .then((res: any) => {
        const content = (res?.data?.payload?.content || []).map((row: any) => ({
          ...row,
          key: row.disbursalId || row.key,
          isInvoice: row.invoiceDocumentId !== null && row.invoiceDocumentId !== undefined
        }));
        setData(content);
        setTotalCount(res?.data?.payload?.totalElements || 0);
      })
      .finally(() => {
        setLoading(false);
        onReconcileDataSequenceComplete?.();
      });
  }, [pageNo, pageSize, filterValues, searchParam, onReconcileDataSequenceComplete]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    preserveSelectedRowKeys: true,
  };

  const handleStatusChange = (key: string, value: string) => {

    if (editingKey === key && statusMap[key] === value) {
      return;
    }
    
    // Close any existing editing row first
    if (editingKey !== null && editingKey !== key) {
      setStatusMap((prev) => {
        const updated = { ...prev };
        // Reset to the original status from data array
        const prevRecord = data.find(item => item.key === editingKey);
        if (prevRecord && prevRecord.status) {
          updated[editingKey] = prevRecord.status;
        } else {
          delete updated[editingKey];
        }
        return updated;
      });
      mainForm.resetFields();
      setEditingKey(null);
      setFormType(null);
    }
    
    // Set status map immediately for UI response
    setStatusMap((prev) => ({ ...prev, [key]: value }));
    
    if (value === "Reconciled") {
      setEditingKey(key);
      setFormType("reconcile");
      const record = data.find((item) => item.key === key);
      if (record) {
        mainForm.resetFields();
        
        // Use lenderDisbursementDate if available, otherwise fallback to disbursalDate
        const disbursementDate = record.lenderDisbursementDate ? moment(record.lenderDisbursementDate) : 
                                (record.disbursalDate ? moment(record.disbursalDate) : null);
        
        // Use lenderDisbursedAmount if available, otherwise fallback to disbursedAmount
        const disbursedAmount = record.lenderDisbursedAmount !== undefined ? 
                              numberWithCommas(record.lenderDisbursedAmount) : 
                              (record.disbursedAmount !== undefined ? numberWithCommas(record.disbursedAmount) : '');
        
        // Use insuranceAmount from record, or default to 0 if undefined
        const insuranceAmt = record.insuranceAmount !== undefined ? 
                           numberWithCommas(record.insuranceAmount) : 
                           '0';
                              
        // Ensure form is populated with the correct values
        const formValues = {
          ...formatReconciliationFormValues(record),
          lenderDisbursementDate: disbursementDate,
          lenderDisbursedAmount: disbursedAmount,
          insuranceAmount: insuranceAmt
        };
        
          mainForm.setFieldsValue(formValues);
          const currentValues = mainForm.getFieldsValue();
          if (!currentValues.insuranceAmount) {
            mainForm.setFieldValue('insuranceAmount', insuranceAmt);
          }
      }
    } else {
      setEditingKey(null);
      setFormType(null);
      mainForm.resetFields();
    }
  };

  const cancelEdit = () => {
    // Remove the status from the map when canceling
    if (editingKey) {
      setStatusMap((prev) => {
        const updated = { ...prev };
        delete updated[editingKey];
        return updated;
      });
    }
    
    setEditingKey(null);
    setFormType(null);
    mainForm.resetFields();
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    try {
      const values = await mainForm.validateFields();
      
      // Get the actual values from the form to check empty string vs undefined
      const invoiceNo = mainForm.getFieldValue('invoiceNo');
      const invoiceDate = mainForm.getFieldValue('invoiceDate');
      
      // Validation: If Invoice No. is entered but Invoice Date is missing, show error
      if (invoiceNo && !invoiceDate) {
        // Apply errors to the appropriate fields
        const fieldErrors: { name: string; errors: string[] }[] = [];
        
        // If invoice no is provided but date is missing
        fieldErrors.push({
          name: 'invoiceDate',
          errors: ['Required']
        });
        
        mainForm.setFields(fieldErrors);
        return;
      }
      
      // Validation: If Invoice Date is entered but Invoice No. is missing, show error
      if (!invoiceNo && invoiceDate) {
        // Apply errors to the appropriate fields
        const fieldErrors: { name: string; errors: string[] }[] = [];
        
        // If invoice date is provided but invoice no is missing
        fieldErrors.push({
          name: 'invoiceNo',
          errors: ['Required']
        });
        
        mainForm.setFields(fieldErrors);
        return;
      }
      
      // Clean values (remove commas from formatted numbers)
      const cleanValues = {
        ...values,
        lenderDisbursedAmount: values.lenderDisbursedAmount ? Number(values.lenderDisbursedAmount.replace(/,/g, '')) : undefined,
        insuranceAmount: values.insuranceAmount ? Number(values.insuranceAmount.replace(/,/g, '')) : 0, // Default to 0 if not provided
        revenueAmount: values.revenueAmount ? Number(values.revenueAmount.replace(/,/g, '')) : undefined,
        revenuePercentage: values.revenuePct ? Number(values.revenuePct) : undefined,
      };
      
      // Format dates for the API
      const formattedLenderDisbursementDate = values.lenderDisbursementDate ? moment(values.lenderDisbursementDate).format('YYYY-MM-DD') : undefined;
      const formattedInvoiceDate = values.invoiceDate ? moment(values.invoiceDate).format('YYYY-MM-DD') : undefined;
      
      // Get the record
      const record = data.find(item => item.key === editingKey);
      if (!record) return;
      
      // Create payload according to the required structure
      const payload = {
        disbursalId: record.disbursalId,
        lenderDisbursementDate: formattedLenderDisbursementDate,
        lenderDisbursementAmount: cleanValues.lenderDisbursedAmount,
        insuranceAmount: cleanValues.insuranceAmount,
        revenuePercentage: cleanValues.revenuePercentage,
        // Only include revenueAmount if it has a value
        ...(cleanValues.revenueAmount ? { revenueAmount: cleanValues.revenueAmount } : {}),
        // Only include invoiceNo if it has a value
        ...(values.invoiceNo ? { invoiceNo: values.invoiceNo } : {}),
        // Only include invoiceDate if it has a value
        ...(formattedInvoiceDate ? { invoiceDate: formattedInvoiceDate } : {}),
        reconcileStatus: "RECONCILED"
      };
      
      // Send data to API
      setLoading(true);
      API_SERVICE.updateReconciliationDisbursal(payload)
        .then((res: any) => {
          // Update status in local state
          setStatusMap((prev) => ({ ...prev, [record.key]: "Reconciled" }));
          
          // Clear editing state
          setEditingKey(null);
          setFormType(null);
          
          // Refresh data
          API_SERVICE.getReconcileDisbursedCases({
            pageNo,
            pageSize,
            status: 'ISSUE',
            ...filterValues
          })
            .then((res: any) => {
              const content = (res?.data?.payload?.content || []).map((row: any) => ({
                ...row,
                key: row.disbursalId || row.key
              }));
              setData(content);
              setTotalCount(res?.data?.payload?.totalElements || 0);
            });
          
          notification.success({ message: "Reconciliation saved successfully." });
        })
        .catch((error: any) => {
          notification.error({ message: "Failed to save reconciliation." });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
      // This catch block is triggered when form validation fails
      // No need to show a notification as Ant Design Form will display the validation errors
    }
  };

  const validateNumber = (_: any, value: string) => {
    if (value === undefined || value === null || value === '') {
      return Promise.reject('This field is required');
    }
    if (!RECONCILE_NUMBER_REGEX.test(value.toString())) {
      return Promise.reject('Please enter numbers only');
    }
    return Promise.resolve();
  };
  // Validation for optional number fields
  const validateOptionalNumber = (_: any, value: string) => {
    if (value && !RECONCILE_NUMBER_REGEX.test(value.toString())) {
      return Promise.reject('Please enter numbers only');
    }
    return Promise.resolve();
  };
  const validatePercentage = (_: any, value: string | number) => {
    if (value === undefined || value === null || value === '') return Promise.resolve();
    const numValue = typeof value === 'number' ? value : Number(value.toString().replace(/,/g, ''));
    if (!RECONCILE_PERCENTAGE_REGEX.test(numValue.toString())) {
      return Promise.reject('Please enter a value between 0 and 100');
    }
    return Promise.resolve();
  };
  // Handle amount input formatting
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    if (rawValue) {
      mainForm.setFieldsValue({
        [fieldName]: numberWithCommas(rawValue)
      });
    } else {
      mainForm.setFieldsValue({
        [fieldName]: ''
      });
    }
  };
  // Handle percentage input (0-100)
  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value) {
      const numValue = Number(value);
      const limitedValue = Math.min(100, numValue);
      mainForm.setFieldsValue({
        revenuePct: limitedValue.toString()
      });
    } else {
      mainForm.setFieldsValue({
        revenuePct: ''
      });
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Disbursal ID',
      dataIndex: 'disbursalId',
      key: 'disbursalId',
      fixed: 'left',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Case ID',
      dataIndex: 'caseId',
      key: 'caseId',
      fixed: 'left',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Lender Identifier',
      dataIndex: 'lenderIdentifier',
      key: 'lenderIdentifier',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Lender',
      dataIndex: 'lenderName',
      key: 'lenderName',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Customer Name',
      dataIndex: 'borrowerName',
      key: 'borrowerName',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Product',
      dataIndex: 'loanProductName',
      key: 'loanProductName',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Disbursement date',
      dataIndex: 'disbursalDate',
      key: 'disbursalDate',
    },
    {
      title: 'Issue',
      dataIndex: 'issueComment',
      key: 'issueComment',
      width: 150,
      ellipsis: true,
      render: (value: any) =>(
        <Tooltip title={value}>
          {value}
        </Tooltip>
      )
    },
    {
      title: 'Issue reason',
      dataIndex: 'issueReason',
      key: 'issueReason',
      width: 150,
      ellipsis: true,
      render: (value: any) =>(
        <Tooltip title={value}>
          {value}
        </Tooltip>
      )
    },
    {
      title: 'Disbursement amount',
      dataIndex: 'disbursedAmount',
      key: 'disbursedAmount',
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Insurance amount',
      dataIndex: 'insuranceAmount',
      key: 'insuranceAmount',
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Sales user hierarchy',
      dataIndex: 'salesUserHierarchy',
      key: 'salesUserHierarchy',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        return (
          <Select
            value={statusMap[record.key] || undefined}
            style={{ width: 120 }}
            onChange={(value) => handleStatusChange(record.key, value)}
            placeholder="Select Status"
            size="small"
            dropdownMatchSelectWidth={false}
            onSelect={(value) => {handleStatusChange(record.key, value);}}
          >
            <Select.Option value="Reconciled">Reconciled</Select.Option>
          </Select>
        );
      },
    },
  ];

  const renderEditableRow = (record: DataType) => {
    const isInvoice = record.isInvoice;
    
    // Ensure form values are properly initialized for this record
    const currentValues = mainForm.getFieldsValue();
    const hasLenderDisbursementDate = !!currentValues.lenderDisbursementDate;
    const hasLenderDisbursedAmount = !!currentValues.lenderDisbursedAmount;
    const hasInsuranceAmount = !!currentValues.insuranceAmount;
    
    if (!hasLenderDisbursementDate || !hasLenderDisbursedAmount || !hasInsuranceAmount) {
      // If values are missing, try to set them again
      const disbursementDate = record.lenderDisbursementDate 
        ? moment(record.lenderDisbursementDate) 
        : (record.disbursalDate ? moment(record.disbursalDate) : null);
        
      const disbursedAmount = record.lenderDisbursedAmount !== undefined 
        ? numberWithCommas(record.lenderDisbursedAmount) 
        : (record.disbursedAmount !== undefined ? numberWithCommas(record.disbursedAmount) : '');
        
      const insuranceAmt = record.insuranceAmount !== undefined 
        ? numberWithCommas(record.insuranceAmount) 
        : '0';
      
      // Update form fields if they're empty
      if (!hasLenderDisbursementDate) {
        mainForm.setFieldValue('lenderDisbursementDate', disbursementDate);
      }
      
      if (!hasLenderDisbursedAmount) {
        mainForm.setFieldValue('lenderDisbursedAmount', disbursedAmount);
      }
      
      if (!hasInsuranceAmount) {
        mainForm.setFieldValue('insuranceAmount', insuranceAmt);
      }
    }
    
    return (
      <div className="reconciliation-edit-row">
        <Form form={mainForm} layout="inline" className="edit-form-inline material-ui-style">
          <Form.Item label="Lender Disbursement Date" name="lenderDisbursementDate" rules={[{ required: true, message: "Required" }]}> 
            <DatePicker format="DD/MM/YYYY" size="small" className="reconciliation-field" disabled={isInvoice} />
          </Form.Item>
          <Form.Item label="Lender Disbursed amount" name="lenderDisbursedAmount" rules={[{ required: true, message: "Required" }, { validator: validateNumber }]}> 
            <Input placeholder="Rs." size="small" className="reconciliation-field" onChange={(e) => handleAmountChange(e, 'lenderDisbursedAmount')} disabled={isInvoice} />
          </Form.Item>
          <Form.Item label="Insurance amount" name="insuranceAmount" rules={[{ required: true, message: "Required" }, { validator: validateNumber }]}> 
            <Input placeholder="Rs." size="small" className="reconciliation-field" onChange={(e) => handleAmountChange(e, 'insuranceAmount')} disabled={isInvoice} />
          </Form.Item>
          <Form.Item label="Revenue %" name="revenuePct" rules={[{ validator: validatePercentage }]}> 
            <Input size="small" className="reconciliation-field" placeholder="%" onChange={handlePercentageChange} disabled={isInvoice} />
          </Form.Item>
          <Form.Item label="Revenue amount" name="revenueAmount" rules={[{ validator: validateOptionalNumber }]}> 
            <Input placeholder="Rs." size="small" className="reconciliation-field" onChange={(e) => handleAmountChange(e, 'revenueAmount')} disabled={isInvoice} />
          </Form.Item>
          <Form.Item 
            label="Invoice No." 
            name="invoiceNo" 
            validateTrigger={["onChange", "submit"]}
            rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const dateValue = getFieldValue('invoiceDate');
                
                // Only check for validation when both values are relevant for comparison
                if (!value && !dateValue) {
                  return Promise.resolve(); // Both are empty, no validation needed
                }
                
                // If invoice date has value but this field is empty/missing
                if (!value && dateValue) {
                  return Promise.reject('Required');
                }
                
                return Promise.resolve();
              },
            }),
          ]}>
            <Input 
              className="reconciliation-field" 
              size="small" 
              placeholder="Invoice No." 
              style={{ width: 120 }} 
              disabled={isInvoice} 
              onChange={(e) => {
                // Only clear errors if the field has a value
                if (e.target.value) {
                  // Clear the error from invoice date field if it exists
                  const dateField = mainForm.getFieldError('invoiceDate');
                  if (dateField && dateField.length > 0) {
                    mainForm.setFields([{
                      name: 'invoiceDate',
                      errors: []
                    }]);
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item 
            label="Invoice Date" 
            name="invoiceDate" 
            validateTrigger={["onChange", "submit"]}
            rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const invoiceNo = getFieldValue('invoiceNo');
                
                // Only check for validation when both values are relevant for comparison
                if (!value && !invoiceNo) {
                  return Promise.resolve(); // Both are empty, no validation needed
                }
                
                // If invoice no has value but this field is missing
                if (!value && invoiceNo) {
                  return Promise.reject('Required');
                }
                
                return Promise.resolve();
              },
            }),
          ]}>
            <DatePicker 
              className="reconciliation-field" 
              format="DD/MM/YYYY" 
              placeholder="Select date" 
              size="small" 
              style={{ width: 120 }} 
              disabled={isInvoice}
              onChange={(date) => {
                // Only clear errors if a date is selected
                if (date) {
                  // Clear the error from invoice no field if it exists
                  const noField = mainForm.getFieldError('invoiceNo');
                  if (noField && noField.length > 0) {
                    mainForm.setFields([{
                      name: 'invoiceNo',
                      errors: []
                    }]);
                  }
                }
              }}
            />
          </Form.Item>
          <div className="action-buttons-container">
            <CheckCircleOutlined onClick={saveEdit} className="action-check-icon" />
            <CloseCircleOutlined onClick={cancelEdit} className="action-close-icon" />
          </div>
        </Form>
      </div>
    );
  };

  return (
    <div className="reconciliation-table-wrapper reconciliation-material-ui">
      <Table
        rowKey="disbursalId"
        rowSelection={rowSelection}
        className="dynamic-pagination-checkbox table-full-width reconciliation-tight-table reconciliation-table-with-footer"
        style={{ '--bgColor': appColors?.appPrimaryColor ?? '' } as React.CSSProperties}
        scroll={{ x: 'max-content' }}
        size="small"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          showSizeChanger: true,
          position: ['bottomRight'],
          current: pageNo,
          pageSize: pageSize,
          total: totalCount,
          onChange: (page, size) => {
            setPageNo(page);
            setPageSize(size || 10);
          },
        }}
        expandable={{
          expandedRowRender: (record) => {
            if (formType === "reconcile" && editingKey === record.key) {
              return renderEditableRow(record);
            }
            return null;
          },
          expandedRowKeys: editingKey ? [editingKey] : [],
          rowExpandable: (record) => record.key === editingKey,
          expandIconColumnIndex: -1
        }}
      />
    </div>
  );
};

export default ReconciliationIssues;
